import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/store/useAuth';
import { getSubscription } from '@/services/consumerApi';
import { PANEL_URL, BRAND } from '@/config/brand';

const MAX_ATTEMPTS = 20;
const POLL_MS = 3000;

export default function BillingSuccess() {
  const [status, setStatus] = useState('checking');
  const [checking, setChecking] = useState(false);
  const refreshMe = useAuth((s) => s.refreshMe);
  const intervalRef = useRef(null);

  const checkSubscription = useCallback(async () => {
    const data = await getSubscription();
    if (data.subscription?.status === 'active' || data.subscription?.status === 'trialing') {
      await refreshMe();
      setStatus('active');
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimeout(() => { window.location.href = PANEL_URL; }, 2000);
      return true;
    }
    return false;
  }, [refreshMe]);

  useEffect(() => {
    let attempts = 0;
    let cancelled = false;

    const tick = async () => {
      attempts += 1;
      try {
        const ok = await checkSubscription();
        if (cancelled || ok) return;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus('pending');
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      } catch {
        if (cancelled) return;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus('pending');
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      }
    };

    tick();
    intervalRef.current = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkSubscription]);

  const handleRefreshMe = async () => {
    setChecking(true);
    try {
      const ok = await checkSubscription();
      if (!ok) {
        await refreshMe();
        setStatus('pending');
      }
    } catch {
      setStatus('pending');
    } finally {
      setChecking(false);
    }
  };

  if (status === 'active') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-6 grid h-20 w-20 place-items-center rounded-full bg-green-50">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Payment successful!</h1>
          <p className="mt-2 text-slate-600">Your subscription is now active. Redirecting to full app…</p>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 grid h-20 w-20 place-items-center rounded-full bg-blue-50">
            <Loader2 size={40} className="animate-spin text-blue-600" />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Setting up your account</h1>
          <p className="mt-2 text-slate-600">
            Your payment was received and we&rsquo;re activating your subscription. This can take up to a minute while payment confirmation finishes.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button onClick={handleRefreshMe} disabled={checking} className="rounded-xl">
              {checking ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : null}
              Check my account
            </Button>
            <Button asChild variant="outline" className="rounded-xl">
              <Link to="/dashboard/billing">Go to Billing</Link>
            </Button>
          </div>
          <p className="mt-6 text-sm text-slate-500">
            Still pending?{' '}
            <a href={`mailto:${BRAND.support.email}`} className="font-medium text-blue-600 hover:underline">
              Contact support
            </a>
            {' '}({BRAND.support.email})
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-center">
        <div className="mx-auto mb-6 grid h-20 w-20 place-items-center rounded-full bg-blue-50">
          <Loader2 size={40} className="animate-spin text-blue-600" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Confirming payment…</h1>
        <p className="mt-2 text-slate-600">Please wait while we verify your subscription.</p>
      </div>
    </div>
  );
}
